// --- React & Redux
import React, { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
// --- Hooks
import { useDialogsActions } from '../hooks';
import { useDialogsForm } from '../forms';
// --- UI Components
import Select from '@dtci/spellbook/Select';
import Typography from '@dtci/spellbook/Typography';
import Input from '@dtci/spellbook/Input';
import TextArea from 'antd/es/input/TextArea';
import { InputLabel } from '../../InputLabel/InputLabel';
import {nodeEnv} from "../../../config/constants";

/**
 * ContactsForm Component
 *
 * This component renders a form for creating/editing/viewing contacts information.
 *
 * @component
 * @param {Object} data - The data object containing contact information.
 * * @returns {JSX.Element} The rendered ContactsForm component.
 */
export const ContactsForm = ({ data }) => {
    const form = useDialogsForm();
    const { typeCodeOptions } = useSelector((s) => s.contacts);

    useEffect(() => {
        form.setValues(data);
    }, [data]);

    // Function to handle the change event of the select input
    const handleChange = (field) => (value) => {
        form.setValue(field, value);
    };

    return (
        <div className="flexBox" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {[
                {
                    key: 'name',
                    label: 'Name',
                    Component: Input,
                    props: { width: 'auto', labelText: 'name' },
                },
                {
                    key: 'formerlyKnownAs',
                    label: 'Formerly Known As',
                    Component: Input,
                    props: { width: 'auto', labelText: 'formerlyKnownAs' },
                },
                {
                    key: 'alternateName',
                    label: 'Alternate Name',
                    Component: Input,
                    props: { width: 'auto', labelText: 'alternateName' },
                },
                {
                    key: 'lobs',
                    label: 'LOBs',
                    Component: TextArea,
                    props: { labelText: 'lobs', width: 'medium' },
                },
                {
                    key: 'canadianOrig',
                    label: 'Canadian Origination',
                    Component: Input,
                    props: { width: 'auto', labelText: 'canadianOrig' },
                },
            ].map(({ key, label, Component, props }) => (
                <InputLabel key={key} label={label}>
                    <Component
                        {...props}
                        value={form.state?.formData?.[key] || ''}
                        onChange={(e) => handleChange(key)(e.target.value)}
                    />
                </InputLabel>
            ))}

            <InputLabel label="Type">
                <Select
                    style={{ marginBottom: 10 }}
                    name="type"
                    useMenuPortal
                    options={typeCodeOptions}
                    value={
                        typeCodeOptions.find(
                            (item) => item.value === form.state?.formData?.typeCode,
                        ) || {}
                    }
                    onChange={(e) => handleChange('typeCode')(e.value)}
                />
            </InputLabel>
        </div>
    );
};

/**
 * useContactsDialog Hook
 *
 * This hook provides functionality to open a contact dialog.
 * It uses the useDialogsActions and useDialogsForm hooks to manage the dialog state.
 * It also provides a handleSubmit function to handle the form submission.
 *
 * @returns {Object} An object containing the openContactsDialog function.
 */
export const useContactsDialog = () => {

    const { openDialog, closeDialog } = useDialogsActions();
    const form = useDialogsForm();

    // Function to handle form submission
    const handleSubmit = useCallback(
        (onSubmit) => () => {
            onSubmit(form.state);
            form.setValues({});
            closeDialog();
        },
        [form, closeDialog],
    );

    const rightslineBaseUrls = {
        development: 'https://admin-int.rightsline.com/Contact/Details',
        qat: 'https://admin-int.rightsline.com/Contact/Details',
        staging: 'https://beta-staging.rightsline.com/mod2',
        production: 'https://beta.rightsline.com/mod2',
        local: 'https://admin-int.rightsline.com/Contact/Details',
    };

    const baseUrl = rightslineBaseUrls[nodeEnv] || rightslineBaseUrls.local;

    // Function to open the legal status dialog
    const openContactsDialog = useCallback(

        (data, title, onConfirm) =>
            openDialog({
                title: (
                    <div style={{ display: 'flex' }}>
                        <span>{title}</span>
                        {data?.rightsLineId && (
                            <>
                                <Typography
                                    variant={'T15'}
                                    style={{ marginLeft: 325, marginTop: 10 }}
                                >
                                    View on Rightsline:
                                </Typography>
                                <a
                                    key={data?.id}
                                    target="_blank"
                                    rel="noreferrer"
                                    href={`${baseUrl}/${data.rightsLineId}`}
                                    style={{
                                        fontSize: 14,
                                        color: '#3611c8',
                                        marginLeft: 5,
                                        textDecoration: 'underline',
                                        marginTop: 10,
                                    }}
                                >
                                    {data?.rightsLineId}
                                </a>
                            </>
                        )}
                    </div>
                ),
                content: <ContactsForm data={data} />,
                closeAction: true,
                primaryAction: {
                    label: 'Save',
                    action: handleSubmit(onConfirm),
                },
            }),
        [openDialog, handleSubmit],
    );

    return { openContactsDialog };
};
